import { LineItem, CustomColumnHeader, CustomColumnItemWithOrderIndex, Global } from 'types';
import { matchesNew } from '@components/bid_request/details/utils';
import { cleanItemValue } from './cleanItemValue';
import { getItemRow, ExportValueType } from './getItemRow';
import { DEFAULT_ITEMS_PER_UNIT_VALUE } from './constants';

interface UnorderedCustomItemsForLineItemProps {
  customColumnHeaders: CustomColumnHeader[];
  global: Global;
  incomingColumnTypes?: string[];
  isSupplier?: boolean;
  lineItem: LineItem;
}

const getUnorderedCustomItemsForLineItem = ({
  customColumnHeaders,
  global,
  incomingColumnTypes = undefined,
  isSupplier = false,
  lineItem,
}: UnorderedCustomItemsForLineItemProps) => {
  const defaultColumnTypes = Object.values(global.lots.custom_column_headers.column_types);
  const columnTypes = incomingColumnTypes || defaultColumnTypes;

  const { custom_column_items: customItems = [] } = lineItem;

  const matchedItemsByType = customItems
    .filter(item => columnTypes.includes(item.column_type));

  // Only show items that are visible to suppliers
  // A custom item should always match to a custom header id, unless it is a new custom header and custom item (through the import process).
  if (isSupplier) {
    return matchedItemsByType
      .filter(item => customColumnHeaders
        .some(header =>
          (header.id === item.custom_column_header_id || matchesNew(item.custom_column_header_id)) &&
          header.is_supplier_visible));
  }

  return matchedItemsByType;
};

interface GenerateItemCellsProps {
  customColumnHeadersDisplayText: string[];
  descItemsList: CustomColumnItemWithOrderIndex[];
}

const generateItemCells = ({
  customColumnHeadersDisplayText,
  descItemsList
}: GenerateItemCellsProps): string[] => {
  // Uses unique display text to maintain order
  return customColumnHeadersDisplayText.map((_ch, index) => {
    const foundItem = descItemsList.find(item => item.column_order_index === index);

    return foundItem ? cleanItemValue(foundItem.display_text) : '';
  });
};

interface ICOConditionProps {
  isCurrentRoundOne: boolean;
  isMultipleRoundBidEvent: boolean;
  lineItem: LineItem;
  setBaselineFromQuotes: boolean;
}

/**
 * Returns true when the ICO zero-out condition is met: multi-round event during round 1
 * with ICO enabled and the system has modified the line item from supplier quotes.
 */
const isICOZeroCondition = ({
  isMultipleRoundBidEvent,
  setBaselineFromQuotes,
  isCurrentRoundOne,
  lineItem,
}: ICOConditionProps): boolean => (
  isMultipleRoundBidEvent &&
  setBaselineFromQuotes &&
  isCurrentRoundOne &&
  Boolean(lineItem.system_modified)
);

const parsePrice = (value: number | string | null | undefined): number => {
  const parsed = parseFloat(String(value));
  return Number.isNaN(parsed) ? 0 : parsed;
};

interface ConstructItemMapProps {
  icoCondition: boolean;
  isPriceUpdate: boolean;
  itemCells: string[];
  lineItem: LineItem;
}

/**
 * Builds the exported cell values for one line item.
 *
 * ICO zeroing targets the column the event negotiates from: a price update has only the agreed
 * price, so zeroing hits the baseline `price`; every other event type negotiates from the starting
 * price, so zeroing hits `negotiation_starting_price` and the baseline `price` always reports its
 * persisted value.
 */
const constructItemMap = ({
  icoCondition,
  isPriceUpdate,
  itemCells,
  lineItem,
}: ConstructItemMapProps): Record<string, ExportValueType> => {
  const pricePerUnit = (isPriceUpdate && icoCondition) ? 0 : parsePrice(lineItem.price);
  const negotiationPricePerUnit = (!isPriceUpdate && icoCondition) ? 0 : parsePrice(lineItem.negotiation_starting_price);
  // Exports the buyer's override only, never the AI predictive value. A row the AI still owns
  // therefore exports blank, which is what the import reads as "leave this to AI pricing" — so a
  // download followed by an unedited re-upload preserves AI ownership instead of pinning every
  // row as a manual override. Uses `!= null` so an explicit $0 override (real buyer intent)
  // flows through instead of being dropped as falsy.
  const suggestedPricePerUnit = lineItem.suggested_price_override != null
    ? parsePrice(lineItem.suggested_price_override)
    : undefined;

  return {
    description: itemCells,
    discount: lineItem.discount,
    extendedQuantity: Number(lineItem.extended_quantity),
    id: lineItem.id,
    name: cleanItemValue(lineItem.name),
    negotiationPricePerUnit,
    noBid: lineItem?.noBid,
    pricePerUnit,
    quantity: Number(lineItem.quantity) || DEFAULT_ITEMS_PER_UNIT_VALUE,
    quantityOfUnits: Number(lineItem.extended_quantity),
    required: Boolean(lineItem.is_required),
    suggestedPricePerUnit,
    surcharge: lineItem?.surcharge,
    unitOfMeasure: lineItem?.units ? cleanItemValue(lineItem.units) : undefined,
  };
};

interface getLineItemRowValuesProps {
  customColumnHeaders: CustomColumnHeader[];
  customColumnHeadersDisplayText: string[];
  customHeaderIdsOrderMap: Record<string, number>;
  exportColumnNames: string[];
  global: Global;
  isCurrentRoundOne?: boolean;
  isMultipleRoundBidEvent?: boolean;
  isPriceUpdate?: boolean;
  isSupplier?: boolean;
  lineItem: LineItem,
  setBaselineFromQuotes: boolean;
}

export const getLineItemRowValues = ({
  lineItem,
  exportColumnNames,
  customHeaderIdsOrderMap,
  customColumnHeaders,
  customColumnHeadersDisplayText,
  global,
  isCurrentRoundOne = false,
  isPriceUpdate = false,
  isSupplier = false,
  isMultipleRoundBidEvent = false,
  setBaselineFromQuotes = false,
}: getLineItemRowValuesProps) => {
  const descriptionItemsType = global.lots.custom_column_headers.column_types.descriptive;

  // Get unordered custom items for the line item
  const unorderedCustomItemsForLineItem = getUnorderedCustomItemsForLineItem({
    customColumnHeaders,
    global,
    incomingColumnTypes: [descriptionItemsType],
    isSupplier,
    lineItem
  });

  // Map custom items to include column order index
  const descItemsList: CustomColumnItemWithOrderIndex[] = unorderedCustomItemsForLineItem
    .map(item => ({ ...item, column_order_index: customHeaderIdsOrderMap[item.custom_column_header_id] }));

  // Generate item cells based on custom column headers display text
  const itemCells = generateItemCells({ customColumnHeadersDisplayText, descItemsList });

  // Check ICO zero-out condition
  const icoCondition = isICOZeroCondition({
    isCurrentRoundOne,
    isMultipleRoundBidEvent,
    lineItem,
    setBaselineFromQuotes,
  });

  // Construct the item map
  const itemMap = constructItemMap({ icoCondition, isPriceUpdate, itemCells, lineItem });

  return getItemRow(itemMap, exportColumnNames);
};
